import ExcelJS from "exceljs";

// Round up to 4 decimal places (handles NaN/Infinity)
export const ceilTo4Decimals = (num: number): number => {
  if (!Number.isFinite(num)) return 0;
  return Math.ceil(num * 10000) / 10000;
};

// Round up to 2 decimal places (handles NaN/Infinity)
export const ceilTo2Decimals = (num: number): number => {
  if (!Number.isFinite(num)) return 0;
  return Math.ceil(num * 100) / 100;
};

// Round down to 2 decimal places (handles NaN/Infinity)
export const floorTo2Decimals = (num: number): number => {
  if (!Number.isFinite(num)) return 0;
  return Math.floor(num * 100) / 100;
};

// Safe number for Excel output (handles NaN/Infinity/undefined)
export const safeNumber = (num: number | undefined | null): number => {
  if (num === undefined || num === null || !Number.isFinite(num)) return 0;
  return num;
};

export const sanitizeEmail = (value: string): string =>
  String(value || "")
    .replace(/\u00A0/g, " ")
    .replace(/[\u200B-\u200D\uFEFF]/g, "")
    .trim();

export const normalizeEmailKey = (value: string): string =>
  sanitizeEmail(value).toLowerCase();

// Parse cell value to number
export const parseCellNumber = (cellValue: ExcelJS.CellValue): number => {
  if (typeof cellValue === "number") {
    return cellValue;
  }
  if (typeof cellValue === "string") {
    const cleaned = cellValue.replace(/,/g, "").replace(/\s/g, "").trim();
    return parseFloat(cleaned) || 0;
  }
  if (cellValue && typeof cellValue === "object" && "result" in cellValue) {
    return Number(cellValue.result) || 0;
  }
  return 0;
};

// Parse cell value to string
export const parseCellString = (cellValue: ExcelJS.CellValue): string => {
  if (cellValue === null || cellValue === undefined) return "";
  if (typeof cellValue === "string") return cellValue.trim();
  if (typeof cellValue === "number") return String(cellValue);
  if (cellValue instanceof Date) {
    // ExcelJS parses dates and JavaScript Date may shift them due to timezone
    // Use UTC methods to get the original date values stored in Excel
    const month = cellValue.getUTCMonth() + 1;
    const day = cellValue.getUTCDate();
    const year = cellValue.getUTCFullYear();
    const hours = String(cellValue.getUTCHours()).padStart(2, "0");
    const minutes = String(cellValue.getUTCMinutes()).padStart(2, "0");
    const seconds = String(cellValue.getUTCSeconds()).padStart(2, "0");
    // Include time if it's not midnight (00:00:00)
    if (hours !== "00" || minutes !== "00" || seconds !== "00") {
      return `${month}/${day}/${year} ${hours}:${minutes}:${seconds}`;
    }
    return `${month}/${day}/${year}`;
  }
  if (typeof cellValue === "object" && "result" in cellValue) {
    return String(cellValue.result);
  }
  return String(cellValue);
};

// Excel date to string helper
// Uses UTC to avoid timezone issues that can shift dates by one day
export const excelDateToString = (serial: number): string => {
  if (!serial || isNaN(serial) || serial < 1000) return String(serial);
  // Excel epoch: December 30, 1899 (accounting for Excel's leap year bug)
  // Calculate days since epoch and convert to UTC date
  const utcDays = Math.floor(serial) - 1; // Subtract 1 because Excel incorrectly treats 1900 as leap year
  const utcDate = new Date(Date.UTC(1900, 0, utcDays));
  const year = utcDate.getUTCFullYear();
  const month = utcDate.getUTCMonth() + 1;
  const day = utcDate.getUTCDate();
  return `${month}/${day}/${year}`;
};

// Check if value is Excel date
export const isExcelDate = (value: unknown): boolean => {
  return typeof value === "number" && value > 40000 && value < 60000;
};

// Default columns for output file
export const DEFAULT_COLUMNS = {
  AccountAlias: "Avtamata",
  ListingExchange: "ARCA",
  CurrencyPrimary: "USD",
  AssetClass: "STK",
  OrderType: "LMT",
  "Buy/Sell": "SELL",
  CommissionCurrency: "USD",
  TradeDate: new Date().toISOString().slice(0, 10).replace(/-/g, ""),
  TradeID: "6531621365",
};

// Normalize bond name to match metadata keys
export const normalizeBondName = (name: string): string => {
  if (!name) return "";
  const lowerName = name.toLowerCase().trim();

  // Match patterns from input file to standard names (Mongolian and English)
  if (lowerName.includes("гялс") || lowerName.includes("gyls")) return "Гялс төгрөг";
  if (lowerName.includes("сэндли") || lowerName.includes("sendly") || lowerName.includes("sendli")) return "Сэндли";
  if (lowerName.includes("аму") && (lowerName.includes("1.1") || lowerName.includes("1-1") || (!lowerName.includes("1.2") && !lowerName.includes("2")))) return "АМУ 1";
  if (lowerName.includes("amu") && (lowerName.includes("1.1") || lowerName.includes("1-1") || (!lowerName.includes("1.2") && !lowerName.includes("2")))) return "АМУ 1";
  if (lowerName.includes("аму") && (lowerName.includes("1.2") || lowerName.includes("2"))) return "АМУ 2";
  if (lowerName.includes("amu") && (lowerName.includes("1.2") || lowerName.includes("2"))) return "АМУ 2";
  // Нөмөр/Nomor bonds - keep each version separate (8.3, 8.5, etc.)
  if (lowerName.includes("нөмөр") || lowerName.includes("номөр") || lowerName.includes("nomor") || lowerName.includes("numur")) {
    if (lowerName.includes("8.5") || lowerName.includes("8-5")) return "Нөмөр 8.5";
    if (lowerName.includes("8.3") || lowerName.includes("8-3")) return "Нөмөр 8.3";
    return "Нөмөр";
  }
  if (lowerName.includes("абтс") || lowerName.includes("abts")) return "АБТС";
  // Лэнд/Lend bonds - keep 2.4 separate from МН
  if (lowerName.includes("лэнд") || lowerName.includes("ленд") || lowerName.includes("land") || lowerName.includes("lend")) {
    if (lowerName.includes("2.4") || lowerName.includes("2-4")) return "Лэнд 2.4";
    return "Лэнд МН";
  }
  if (lowerName.includes("хбүц") || lowerName.includes("gsb")) return "ХБҮЦ";
  if (lowerName.includes("шунхлай") || lowerName.includes("shunhlai") || lowerName.includes("shunkhlai")) {
    if (
      lowerName.includes("bond 2") ||
      lowerName.includes("бонд 2") ||
      (lowerName.includes(" 2") && !lowerName.includes("3.1") && !lowerName.includes("3-1") && !lowerName.includes("3.3") && !lowerName.includes("3-3"))
    ) return "Шунхлай 2";
    if (lowerName.includes("3.3") || lowerName.includes("3-3")) return "Шунхлай 3.3";
    if (lowerName.includes("3.1") || lowerName.includes("3-1")) return "Шунхлай 3.1";
    return "Шунхлай";
  }
  if (lowerName.includes("альфа") || lowerName.includes("alpha")) return "Альфа";
  if (lowerName.includes("хүннү") || lowerName.includes("hunnu")) return "Хүннү";
  if (lowerName.includes("платин") || lowerName.includes("platin") || lowerName.includes("platinum")) return "Платин";
  if (lowerName.includes("emerald")) return "Emerald";
  if (lowerName.includes("national") && lowerName.includes("investment")) return "National Investment";
  if (lowerName.includes("tsogts") || lowerName.includes("цогц")) return "Цогц зээл";
  // Нео/Neo bonds - keep each version separate (1.1, 1.2, 1.3, 1.4, 1.5, etc.)
  if ((lowerName.includes("нео") || lowerName.includes("neo")) && (lowerName.includes("1.5") || lowerName.includes("1-5"))) return "Нео 1.5";
  if ((lowerName.includes("нео") || lowerName.includes("neo")) && (lowerName.includes("1.4") || lowerName.includes("1-4"))) return "Нео 1.4";
  if ((lowerName.includes("нео") || lowerName.includes("neo")) && (lowerName.includes("1.3") || lowerName.includes("1-3"))) return "Нео 1.3";
  if ((lowerName.includes("нео") || lowerName.includes("neo")) && (lowerName.includes("1.2") || lowerName.includes("1-2"))) return "Нео 1.2";
  if ((lowerName.includes("нео") || lowerName.includes("neo")) && (lowerName.includes("1.1") || lowerName.includes("1-1"))) return "Нео 1.1";
  if (lowerName.includes("нео") || lowerName.includes("neo")) return "Нео 1.1"; // Default to 1.1 if no version specified

  // Return original name if no match (first letter capitalized)
  return name.charAt(0).toUpperCase() + name.slice(1).toLowerCase();
};

// Default Bond Metadata
export const DEFAULT_BOND_METADATA: Record<string, { rate: number; frequency: string; quarterDays: number; paymentDates: string[] }> = {
  "Альфа": { rate: 0.195, frequency: "quarterly", quarterDays: 91, paymentDates: [] },
  "Хүннү": { rate: 0.202, frequency: "quarterly", quarterDays: 91, paymentDates: [] },
  "Платин": { rate: 0.20, frequency: "quarterly", quarterDays: 91, paymentDates: [] },
  "Лэнд МН": { rate: 0.185, frequency: "quarterly", quarterDays: 91, paymentDates: ["01/31", "04/30", "07/31", "10/31"] },
  "Сэндли": { rate: 0.20, frequency: "quarterly", quarterDays: 91, paymentDates: ["03/31", "06/30", "09/30", "12/30"] },
  "Нөмөр": { rate: 0.195, frequency: "quarterly", quarterDays: 91, paymentDates: ["04/27", "07/27", "10/27", "01/27"] },
  "АБТС": { rate: 0.20, frequency: "quarterly", quarterDays: 91, paymentDates: ["04/30", "07/30", "10/30", "01/30"] },
  "АМУ 1": { rate: 0.185, frequency: "quarterly", quarterDays: 91, paymentDates: ["05/12", "08/12", "11/11", "02/11"] },
  "АМУ 2": { rate: 0.19, frequency: "quarterly", quarterDays: 91, paymentDates: ["05/19", "08/19", "11/19", "02/19"] },
  "Шунхлай": { rate: 0.17, frequency: "semi-annual", quarterDays: 182, paymentDates: ["06/27", "12/27"] },
  "Шунхлай 2": { rate: 0.17, frequency: "semi-annual", quarterDays: 182, paymentDates: ["07/07", "01/07"] },
  "Шунхлай 3.1": { rate: 0.17, frequency: "semi-annual", quarterDays: 182, paymentDates: ["06/27", "12/27"] },
  "Шунхлай 3.3": { rate: 0.17, frequency: "semi-annual", quarterDays: 182, paymentDates: ["06/27", "12/27"] },
  "ХБҮЦ": { rate: 0.18, frequency: "quarterly", quarterDays: 91, paymentDates: ["04/04", "07/04", "10/04", "01/04"] },
  "GSB-ABS ХБҮЦ": { rate: 0.18, frequency: "quarterly", quarterDays: 91, paymentDates: [] },
  "GSB-ABS": { rate: 0.18, frequency: "quarterly", quarterDays: 91, paymentDates: [] },
  "Нео 1.1": { rate: 0.20, frequency: "quarterly", quarterDays: 91, paymentDates: [] },
  "Нео 1.2": { rate: 0.20, frequency: "quarterly", quarterDays: 91, paymentDates: [] },
  "Нео 1.3": { rate: 0.20, frequency: "quarterly", quarterDays: 91, paymentDates: [] },
  "Нео 1.4": { rate: 0.20, frequency: "quarterly", quarterDays: 91, paymentDates: [] },
  "Нео 1.5": { rate: 0.20, frequency: "quarterly", quarterDays: 91, paymentDates: [] },
  "Гялс төгрөг": { rate: 0.18, frequency: "quarterly", quarterDays: 91, paymentDates: ["03/31", "06/30", "09/30", "12/30"] },
  "Emerald": { rate: 0.20, frequency: "quarterly", quarterDays: 91, paymentDates: [] },
  "National Investment": { rate: 0.20, frequency: "quarterly", quarterDays: 91, paymentDates: [] },
  "Цогц зээл": { rate: 0.20, frequency: "quarterly", quarterDays: 91, paymentDates: [] },
};
